import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { MainHeadline } from 'components/Typography'
import { NavButton, OrangeButton, TextButton } from 'components/Button'
import { saveCalendarConfirm, updateEditState } from 'reducks/confirm/operations'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getStoreRef } from 'reducks/auth/selectors'
import { DaysOfWeek, DayOfWeekType } from 'lib/daysOfWeek'
import { getHolidays } from 'reducks/store/selectors'
import { useDaysOfWeekCheckbox } from 'hooks/form'
import { HolidayCard } from 'components/Card'
import { BusinessHourForm } from 'components/Form'
import { useBusinessTimes, useRemainingDaysOfWeek } from 'hooks/businessHour'
import { parseToBusinessTime, parseToOpenTime } from 'lib/businessHour'
import { fetchStore } from 'reducks/store/operations'
import { getIsBeingEdited } from 'reducks/confirm/selectors'
import { StoreSettingError } from 'components/Error'
const daysOfWeek = new DaysOfWeek()

const BusinessHourTemplate: FC = () => {
  const dispatch = useDispatch()
  const isBeingEdited = useSelector((state: State) => getIsBeingEdited(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))

  const {
    addBusinessTime,
    businessTimes,
    handleBusinessTimes,
    selectAllDaysOfWeek,
    selectDaysOfWeek,
    unselectAllDaysOfWeek,
  } = useBusinessTimes()

  const currentHolidays = useSelector((state: State) => getHolidays(state))
  const [holidays, setHolidays] = useState<DayOfWeekType[]>([])
  const handleHolidays = useDaysOfWeekCheckbox(setHolidays)
  const remainingDaysOfWeek = useRemainingDaysOfWeek(businessTimes, holidays)
  const selectableHolidays = useMemo(() => {
    return daysOfWeek.getDaysOfWeek().filter((dayOfWeek) => {
      // 選択中の曜日であれば残す
      if (holidays.includes(dayOfWeek)) return true
      // 営業時間として選択中の曜日は除外
      return !businessTimes.some((businessTime) => businessTime.days_of_week.includes(dayOfWeek))
    })
  }, [holidays, businessTimes])
  const selectAllHolidays = useCallback(() => setHolidays([...selectableHolidays]), [setHolidays, selectableHolidays])
  const unselectAllHolidays = useCallback(() => setHolidays([]), [setHolidays])

  const confirmSetting = useCallback(() => {
    const open_time = parseToOpenTime(businessTimes)
    const order_start_time = parseToBusinessTime(businessTimes, 'order_start_time')
    const last_order_time = parseToBusinessTime(businessTimes, 'last_order_time')
    const last_pickup_time = parseToBusinessTime(businessTimes, 'last_pickup_time')
    const relative_pickupable_time = parseToBusinessTime(businessTimes, 'relative_pickupable_time')
    dispatch(
      saveCalendarConfirm(
        holidays,
        open_time,
        order_start_time,
        last_order_time,
        last_pickup_time,
        relative_pickupable_time
      )
    )
  }, [dispatch, holidays, businessTimes])

  useEffect(() => {
    if (!storeRef || isBeingEdited) return
    dispatch(fetchStore(storeRef))
    dispatch(updateEditState(true))
  }, [storeRef])

  useEffect(() => {
    setHolidays([...currentHolidays])
  }, [currentHolidays])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline id={'day-of-week'} headline={'営業時間・定休日を修正する'} />
      <Spacer size={28} />
      {businessTimes.map((businessTime, index) => {
        const selectableDaysOfWeek = daysOfWeek.getDaysOfWeek().filter((dayOfWeek) => {
          // 選択中の曜日であれば残す
          if (businessTime.days_of_week.includes(dayOfWeek)) return true
          // 定休日か別の営業時間カードで選択中の曜日は除外
          return (
            !holidays.includes(dayOfWeek) &&
            !businessTimes.some((_businessTime) => _businessTime.days_of_week.includes(dayOfWeek))
          )
        })
        const selectAll = () => selectAllDaysOfWeek(selectableDaysOfWeek, index)
        const unselectAll = () => unselectAllDaysOfWeek(index)
        return (
          <React.Fragment>
            <BusinessHourForm
              key={index.toString()}
              index={index}
              daysOfWeek={businessTime.days_of_week}
              openTime={businessTime.open_time}
              orderStartTime={businessTime.order_start_time}
              lastOrderTime={businessTime.last_order_time}
              lastPickupTime={businessTime.last_pickup_time}
              relativePickupableTime={businessTime.relative_pickupable_time}
              handleBusinessTimes={handleBusinessTimes}
              selectDaysOfWeek={selectDaysOfWeek}
              selectableDaysOfWeek={selectableDaysOfWeek}
              selectAll={selectAll}
              unselectAll={unselectAll}
            />
            <Spacer size={12} />
          </React.Fragment>
        )
      })}
      <TextButton
        disabled={
          remainingDaysOfWeek.length === 0 ||
          businessTimes.some((businessTime) => businessTime.days_of_week.length === 0)
        }
        iconPath={
          remainingDaysOfWeek.length === 0 ||
          businessTimes.some((businessTime) => businessTime.days_of_week.length === 0)
            ? '/icon/grey/plus.svg'
            : '/icon/blue/plus.svg'
        }
        label={'営業時間を追加する'}
        onClick={addBusinessTime}
      />
      <Spacer size={28} />
      <HolidayCard
        holidays={holidays}
        handleHolidays={handleHolidays}
        selectableDaysOfWeek={selectableHolidays}
        selectAll={selectAllHolidays}
        unselectAll={unselectAllHolidays}
      />
      <Spacer size={40} />
      <OrangeButton
        disabled={remainingDaysOfWeek.length !== 0}
        label={'保存する'}
        onClick={confirmSetting}
        size={'medium'}
        type={'main'}
      />
      {(businessTimes.some((businessTime) => Object.values(businessTime).some((time) => time === '')) ||
        remainingDaysOfWeek.length !== 0) && (
        <>
          <Spacer size={24} />
          <StoreSettingError businessTimes={businessTimes} remainingDaysOfWeek={remainingDaysOfWeek} />
        </>
      )}
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default BusinessHourTemplate
